export const componentTranslations = {
    clientDashboard: {
        welcome: 'Welcome to BEEP',
        intro: 'The Borrower Engagement & Evaluation Platform (BEEP) is a system CIC has developed to track essential client information, primarily through the use of periodic surveys. This information will help us gain insight into:',
        listItem1: 'The performance of your business',
        listItem2: 'The impact of our loans',
        listItem3: 'Your level of satisfaction with CIC',
        listItem4: 'What we can do to better serve you and the community',
        privacyStatement:
            'The data you submit to us via this platform is kept confidential and will only be reviewed by CIC staff.',
        instruction:
            '<highlight><a><strong>Please visit the Surveys page to complete any outstanding surveys.</strong></a></highlight> We thank you in advance for your cooperation!',
        questions:
            'Questions? Please feel free to email us at <highlight><emailLink>lending@cictucson.org</emailLink></highlight> or reach out to your Relationship Manager.'
    },
    stagingEnvBanner: {
        content: 'UAT Environment'
    },
    NumberOrNoAnswerRadioGroup: {
        tooltipLabelKey: 'Clicking on the numeric input will deselect any previously selected option.'
    },
    contactsModule: {
        newContact: 'New Contact',
        editingContact: 'Editing Contact',
        toggleLabelPlural: 'Show {{contactCount}} registered contacts',
        toggleLabelSingular: 'Show {{contactCount}} registered contact',
        description:
            "Only contacts who have already been registered with this organization can be selected as owners/executive team members. If an owner/executive team member is not shown in the table below, then they must be registered. To register a new contact, please click on 'Show registered contacts', displayed below, to expand the contacts module, then click on the 'New Contact' button."
    },
    createSurvey: {
        newSurvey: 'New Survey',
        editingSurvey: 'Create New Survey'
    },
    loansTable: {
        newLoan: 'New Loan'
    },
    clientsTable: {
        newClient: 'New Client'
    },
    usersTable: {
        newUser: 'New User'
    },
    confirmContactsModule: {
        ownerNumber: `#{{number}}: {{name}}`
    },
    modals: {
        confirmDiscardChanges: {
            title: 'Discard changes?',
            body: "You have made changes that haven't been saved yet. Are you sure you want to discard them?",
            confirmCTA: 'Yes, Discard Changes'
        },
        primaryReassigned: {
            title: 'Primary Contact Successfully Reassigned',
            body: 'You have successfully transferred the primary contact role to another contact on this organization. They will be now be contacted to fill out this and future surveys.'
        },
        omitted: {
            loanGroups: {
                title: 'Loan groups omitted from batch',
                body: 'Some loan groups were omitted from the batch of generated surveys. This can happen for two reasons. Either an error was encountered during survey generation or the computed target range results in one already covered by an existing survey of the same type.'
            },
            organizations: {
                title: 'Organizations omitted from batch',
                body: 'Some organizations were omitted from the batch of generated surveys. This can happen for two reasons. Either an error was encountered during survey generation or the computed target range results in one already covered by an existing survey of the same type.'
            }
        },
        confirmDeleteContact: {
            title: 'Delete contact?',
            body: 'Are you sure you want to delete this contact?',
            confirmCTA: 'Yes, Delete Contact'
        },
        cannotDeleteContact: {
            title: 'This contact cannot be deleted',
            body: 'There are several reasons why a contact cannot be deleted.',
            reason1: 'A user cannot delete their own contact',
            reason2: 'A contact cannot be deleted if they are the primary user of an organization.',
            reason3: 'A contact cannot be deleted if they have an associated user who has access to the BEEP platform.',
            solutions:
                'If you want to delete a contact please first reassign the primary contact to someone else and revoke their access to BEEP by clicking the <highlight>Revoke Credentials</highlight> button (if it is visible beneath their avatar on the left). After these steps have been taken, return here and click the <highlight>Delete</highlight> button again to delete this contact.'
        },
        inviteUser: {
            title: 'Send Invite?',
            body: 'An invite email will be dispatched to the following email: {{email}}',
            confirmCTA: 'Yes, Send Invite'
        },
        revokeCredentials: {
            title: 'Revoke Credentials?',
            body: 'Access to the BEEP platform will be revoked for the following email: {{email}}',
            confirmCTA: 'Yes, Revoke Access'
        },
        saveDirty: {
            title: 'Unsaved changes',
            body: 'You are attempting to navigate to another form section while you have unsaved changes. To close this modal and review this form section again click <highlight>Ok</highlight>. Wehn satisfied, click the <highlight>Save & Continue</highlight> button. If you wish to discard any unsaved changes and navigate away just click the <highlight>Discard Unsaved Changes</highlight> button.',
            confirmCTA: 'Save and Proceed'
        },
        enrollOrg: {
            title: 'Enroll Client in Surveys?',
            body: 'Once a client has been enrolled, they will receive email notifications about surveys which are ready for their attention. Are you ready to enroll this client now?',
            confirmCTA: 'Yes, Enroll Client'
        },
        confirmEmail: {
            title: 'Confirm Login Email',
            body: 'Please provide your email for confirmation'
        }
    },
    endingSlide: {
        youreAllSet: "You're all set!",
        actions: {
            continueToDashboard: 'Continue to Dashboard',
            returnToSurveys: 'Return to Surveys'
        }
    },
    confirmPrimaryContactSlide: {
        name: 'Confirm Primary Contact',
        description:
            'Before beginning the survey, please confirm that you are the primary contact for this organization. If not, please assign a different primary contact in the dropdown field below.',
        description2:
            "If you need to edit a contact or create a new one, please click on 'Show registered contacts' below to expand the contacts module, then click on the 'New Contact' button.",
        modal: {
            title: 'Are you sure?',
            body: 'The individual identified as the Primary Contact is the sole person who may provide or update information on behalf of an organization. By proceeding, you acknowledge that you will immediately lose the capability to continue with this form.',
            confirmCTA: 'Yes, change primary contact'
        }
    },
    tables: {
        common: {
            resultsPerPage: 'results per page',
            clearFilters: 'Clear All Filters',
            defaultSearchPlaceholder: 'Search...'
        },
        clients: {
            columns: {
                name: 'Name',
                dba: 'Doing Business As',
                relationshipManager: 'Relationship Manager',
                primaryContact: 'Primary Contact',
                isOkayToContact: 'Okay to Contact?',
                hasActiveLoan: 'Has active loan?'
            }
        },
        contacts: {
            columns: {
                name: 'Contact Name',
                organization: 'Client/Org Name',
                primaryPhone: 'Primary Phone',
                primaryEmail: 'Primary Email',
                isOkayToContact: 'Okay to Contact?'
            }
        },
        loans: {
            columns: {
                loanNumber: 'Loan #',
                organization: 'Client/Org Name',
                approvedLoanAmount: 'Approved Amount',
                settlementDate: 'Settlement Date',
                terminationDate: 'Termination Date',
                loanOfficer: 'Loan Officer',
                loanProgram: 'Loan Program'
            }
        },
        loanGroups: {
            columns: {
                name: 'Name',
                originDate: 'Origin Date',
                groupFinancingTotal: 'Total Amount',
                enrolled: 'Is Enrolled?'
            },
            rightControlLabel: 'New Loan Group',
            drawer: {
                title: {
                    new: 'New Loan Group',
                    edit: 'Editing Loan Group'
                }
            }
        },
        surveys: {
            columns: {
                type: 'Type',
                organization: 'Client/Org Name',
                relationshipManager: 'Relationship Manager',
                status: 'Status',
                createdAt: 'Created at',
                dateSent: 'Date sent',
                reminderSent: 'Reminder Sent',
                loanGroup: 'Loan Group',
                reasonOmitted: 'Reason Omitted',
                updatedAt: 'Updated at',
                id: 'ID'
            }
        },
        users: {
            columns: {
                authEmail: 'Authenticaion Email',
                organization: 'Client/Org Name',
                name: 'Name',
                role: 'Role'
            }
        }
    },
    cards: {
        client: {
            header: 'Client Details',
            alert: {
                onboarding: {
                    title: 'Onboarding Pending',
                    body: 'Client has been enrolled, however the associated Primary Contact has not yet visited BEEP.<br /><br />Invite First Sent: {{firstSent}}<br />Invite Last Sent: {{lastSent}}'
                },
                readOnly: {
                    title: 'Editing disabled',
                    body: 'Editing is not allowed while this organization has an active survey in progress.'
                }
            },
            drawer: {
                title: 'Editing Client'
            }
        },
        contact: {
            header: 'Contact Details',
            drawer: {
                title: 'Editing Contact'
            }
        },
        loan: {
            header: 'Loan Details',
            drawer: {
                title: 'Editing Loan'
            }
        },
        user: {
            header: 'User Details',
            drawer: {
                titleEditing: 'Editing User',
                titleNew: 'New User'
            }
        },
        surveyBatch: {
            drawer: {
                title: {
                    batch: 'Generate Survey Batch',
                    single: 'Generate Survey'
                },
                body: {
                    biannual: {
                        batch: 'Biannual surveys are generated in batches. All clients who have <highlight><i>not</i></highlight> received a survey of the type(s) selected in the <highlight>Considered Past Surveys</highlight> field in the form below, within the selected <highlight>Time Period</highlight> leading up to today, will have a new biannual survey generated for them.',
                        single: 'On submission of this form, a survey will be generated for this client if they have <highlight><i>not</i></highlight> received a survey of the type(s) selected in the <highlight>Considered Past Surveys</highlight> field in the form below, within the selected <highlight>Time Period</highlight> leading up to today.'
                    },
                    financial: {
                        batch: 'Financial surveys are generated in batches. All clients who have <highlight><i>not</i></highlight> received a financial survey for the chosen target year will have a new one generated for them.',
                        single: 'On submission of this form, a financial survey will be generated for this client if they have <highlight><i>not</i></highlight> already received one for the chosen target year.'
                    }
                }
            }
        }
    },
    infoTables: {
        name: 'Name',
        pronouns: 'Pronouns',
        suffix: 'Suffix',
        honorific: 'Honorific',
        gender: 'Gender',
        email: 'Email',
        primaryEmail: 'Primary email',
        secondaryEmail: 'Secondary email',
        phone: 'Phone',
        primaryPhone: 'Primary phone',
        homePhone: 'Home phone',
        mobilePhone: 'Mobile phone',
        portfolId: 'Portfol ID',
        race: 'Race',
        isOkayToContact: 'Okay to Contact?',
        isNonProfit: 'Is non-profit?',
        hasActiveLoan: 'Has active loan?',
        loanNumber: 'Loan number',
        loanOfficer: 'Loan officer',
        approvedLoanAmount: 'Approved loan amount',
        effectiveDateOfTermination: 'Termination date',
        actualSettlementDate: 'Settlement date',
        isClosed: 'Is closed?',
        isActive: 'Is active?',
        organizationName: 'Organization name',
        createdAt: 'Created at',
        updatedAt: 'Updated at',
        clientNumber: 'Client number',
        relationshipManager: 'Relationship manager',
        businessName: 'Business name',
        daysAfterFiscalYearEnd: 'Days After Fiscal Year Ends that Report is Due',
        businessType: 'Type of business',
        yearEstablished: 'Date Established',
        formOfOrg: 'Form of organization',
        monthFiscalYearEnds: 'Month fiscal year ends',
        typeOfBusiness: 'Type of business',
        ein: 'EIN',
        dba: 'Doing business as',
        primaryContact: 'Primary contact',
        contact: 'Associated Contact',
        disabled: 'Is disabled?',
        firebaseEmail: 'Authentication Email',
        firebaseId: 'Authentication ID',
        organization: 'Organization',
        hasSignedAgreement: 'Has Signed Agreement?',
        cicRole: 'CIC Role',
        orgRole: 'Organization Role'
    },
    inputValueTable: {
        tooltips: {
            otherExpenses:
                "'Other expenses' amount is automatically generated to account for the difference between the total amount and the sum of each provided line item. It cannot be manually edited and will be removed from the table automatically once the line item totals matches the stated total amount.",
            consolidateTotal: 'Calculate total based on provided line items.'
        },
        placeholders: {
            label: 'Enter Description...',
            amount: 'Enter Amount...'
        },
        errors: {
            totalTooLow: 'Total amount cannot be less than the sum of the amounts listed.'
        }
    },
    buttons: {
        seeMore: 'See more',
        seeLess: 'See less'
    },
    wageRanger: {
        title: 'Wage Range',
        employee: {
            assignedProgress: '<strong>{{unassigned}}</strong> of <strong>{{total}}</strong> employees unassigned',
            allAssigned: 'All <strong>{{total}}</strong> employees have been assigned.',
            tooMany:
                'You have assigned <strong>{{excess}}</strong> too many employees. The total number of employees should equal <strong>{{total}}</strong>.'
        },
        contractor: {
            assignedProgress: '<strong>{{unassigned}}</strong> of <strong>{{total}}</strong> contractors unassigned',
            allAssigned: 'All <strong>{{total}}</strong> contractors have been assigned.',
            tooMany:
                'You have assigned <strong>{{excess}}</strong> too many contractors. The total number of contractors should equal <strong>{{total}}</strong>.'
        },
        rangeLabelPrefix: 'Tier {{index}}',
        total: 'Total',
        fullTime: 'Full-Time',
        partTime: 'Part-Time',
        fullPart: 'Full/Part-time'
    },
    wageRangeChart: {
        xAxis: 'Wage Range',
        yAxis: 'Employee Count',
        title: 'Salary/Wage chart'
    },
    formStepper: {
        inactiveStepTooltip: 'Form section not relevant based on previous answers.'
    },
    reviewAnswers: {
        title: 'Review & Submit',
        body: "Please review your answers in the summary table below. If you wish to make any changes click on the question and you will be brought back to that answer. If everything looks good then just click 'Submit'!"
    },
    providerButtons: {
        google: 'Sign in with Google',
        microsoft: 'Sign in with Microsoft'
    },
    emailLogin: {
        message:
            'Enter the email you have registered with CIC and click the button below. You will then be emailed a link to get logged in.',
        toAdmin: 'If you are from CIC, please login <a>here</a>.',
        toClient: 'If you are client of CIC, please login <a>here</a>.'
    },
    changeSurveyStatusButton: {
        buttonLabel: 'Approve or Reject',
        radio: {
            title: 'Select an action',
            options: {
                approve: 'Approve',
                reject: 'Reject'
            }
        },
        textarea: {
            label: 'Client feedback',
            placeholder: 'Leave some feedback for the client here..'
        },
        modal: {
            title: 'Review Submission',
            highlightedQuestions: 'You have selected {{questionCount}} question(s) for the client to review'
        }
    },
    returnSurveyToSubmittedButton: {
        buttonLabel: 'Reopen Survey',
        modal: {
            title: 'Reopen Survey?',
            body: "Reopening this survey will be return it to a 'Submitted' state. From there, it can be reviewed and passed back to the client for further changes. Would you like to proceed?",
            confirmCTA: 'Yes, Reopen Survey'
        },
        notifications: {
            success: {
                title: 'This survey has been reopened.'
            },
            fail: {
                title: 'There was an error reopening this survey.'
            }
        }
    },
    surveyNotesButton: {
        buttonLabel: 'View survey notes',
        modal: {
            title: 'Survey Notes'
        },
        noteType: {
            APPROVE: 'Approved',
            REJECT: 'Rejected',
            NOTE_INTERNAL: 'Internal note',
            NOTE_EXTERNAL: 'External note'
        }
    },
    latestReviewNote: {
        guidance:
            "Please navigate to questions/sections highlighted in orange and make any necessary adjustments. Note that some requested changes may be a few pages into a section; if you don't see the orange highlights right away, click <highlight>Save & Continue</highlight> to get there.",
        type: {
            REJECT: 'Requires Adjustment'
        }
    },
    accountSettings: {
        fields: {
            name: {
                label: 'Name',
                placeholder: 'Your name...'
            },
            email: {
                label: 'Email',
                description:
                    'This is the email you use to login to the BEEP platform. Once changed, you will be required to use the new email when logging in.',

                disabledDescription:
                    'This is the email you use to login to the BEEP platform. CIC users cannot change authentication email.',
                placeholder: 'The email you wish to login with...'
            },
            preferredLang: {
                label: 'Preferred language'
            }
        }
    },
    loanGroupForm: {
        fields: {
            name: {
                label: 'Loan Group Name',
                placeholder: '...'
            },
            loans: {
                label: 'Included Loans',
                description:
                    'Loans with a settlement date which predates the origin date of this loan group may not be included',
                placeholder: 'Select one or more'
            },
            enrolled: {
                label: 'Enrolled',
                placeholder: ''
            },
            originDate: {
                label: 'Origin Date',
                placeholder: ''
            },
            groupFinancingTotal: {
                label: 'Group Financing Total',
                placeholder: ''
            },
            notes: {
                label: 'Notes',
                placeholder: 'Leave any notes for CIC users here...'
            }
        }
    },
    createSurveyForm: {
        fields: {
            surveyTrigger: {
                label: 'Survey type',
                placeholder: '...'
            },
            targetSurveys: {
                label: 'Considered Past Surveys',
                placeholder: '...'
            },
            targetWindow: {
                label: 'Time Period',
                placeholder: 'Select timeframe...'
            },
            targetYear: {
                label: 'Target Year',
                placeholder: 'Select year...'
            }
        }
    },
    usersTables: {
        cicUsers: 'CIC Users',
        allUsers: 'All Users'
    },
    userCard: {
        rolesFieldLabel: 'User roles',
        roles: {
            ORG_USER: 'Organization User',
            ORG_PRIMARY: 'Organization Primary User',
            CIC_USER: 'CIC User',
            CIC_ADMIN: 'CIC Admin',
            CIC_OWNER: 'CIC Super Admin'
        }
    },
    onboardingModal: {
        title: 'Onboarding',
        begin: 'Please click the button below to start the onboarding survey for this organization',
        continue: 'Please click the button below to continue the onboarding survey for this organization'
    },
    mustOnboardAlert: {
        title: 'Onboarding is incomplete',
        body: 'Your organizations primary contact must complete the onboarding process before any other members or said organization may use the BEEP platform'
    },
    userRoleLabels: {},
    clientCard: {
        enrollAlert: {
            primaryContactRequired:
                'In order to enroll this client in surveys, you must to assign a primary contact. To do this: <br><br> 1. Select the <highlight>Contacts tab</highlight> on the card at the bottom of this page<br> 2. Click the <highlight>+ New Contact</highlight> button<br> 3. Fill out the contact details form and click <highlight>Save & Finish</highlight><br> 4. Then, click <highlight>Edit</highlight> on the <highlight>Client Details</highlight> card<br> 5. Select your newly created contact from the primary contact dropdown and click <highlight>Save & Finish</highlight><br><br>',
            default:
                "Please review the client information below and make any necessary adjustments. Once you are satisfied that they are ready to start using the BEEP platform, click the 'Enroll' button and we will reach out to their primary contact to initialize the onboarding process."
        }
    },
    surveyBatch: {
        cta: 'Generate Survey Batch'
    },
    sendSurveyButton: {
        cta: 'Dispatch Survey',
        modal: {
            title: 'Dispatch Survey?',
            body: 'Once dispatched, the primary contact for this organization will be notified and may begin filling out the survey. Do you wish to proceed?',
            confirmCTA: 'Yes, Dispatch Survey'
        }
    },
    sendReminderButton: {
        cta: 'Send Reminder',
        modal: {
            title: 'Send Reminder?',
            reminderSent: 'Last reminder sent on {{reminderSentDate}}',
            body: 'This will dispatch an email notification to the clients primary contact, reminding them to fill out this survey. Do you wish to proceed?',
            confirmCTA: 'Yes, Dispatch Reminder'
        }
    },
    warnAboutMobileStyles: {
        modal: {
            title: 'Mobile Styling Not Yet Optimized',
            body: 'For the smoothest experience, we recommend using the BEEP platform on a desktop/laptop computer or tablet. Mobile styling has not yet been properly implemented so you may encounter issues using it on a mobile device.'
        }
    },
    deleteSurveyButton: {
        modal: {
            title: 'Delete Survey?',
            body: {
                onboarding:
                    'Once deleted, the clients onboarding status will be reset. A new onboarding survey will be generated and served to the primary contact for this organization when they next login. Do you wish to proceed?',
                intake: 'Once deleted, a new intake survey may be generated by opening the associated loan group details form, toggling <highlight>Enrolled</highlight> radio button to <highlight>No</highlight> and saving, then reopening the same form and toggling <highlight>Enrolled</highlight> back to <highlight>Yes</highlight>. By re-enrolling a loan group this way, a new intake survey is generated. Do you wish to proceed?',
                biannual:
                    'Once deleted, this survey will no longer be available to dispatch to the client until a new batch of surveys is generated. Do you wish to proceed?'
            },
            confirmCTA: 'Yes, Delete Survey'
        }
    },
    loanGroupSurveysSummary: {
        title: 'Surveys in this loan group'
    },
    surveyReview: {
        alert: 'If you notice incorrect or incomplete data in the submitted survey, before you reject the submission, click on the problem questions to select them for highlighting. When a survey is rejected, it is passed back to the client so that they may make any required amendments. Any questions that were selected by the reviewer prior to rejecting the submission will be highlighted for the client, making it easier for them to know which questions require a second look.<br/><br/>In addition, after clicking “Approve or Reject,” please provide detailed information (e.g. data they filled out) in the Client Feedback box. This will not only help the client, but will help you remember what changes to look for when reviewing newly re-submitted information.'
    },
    profitLossStatement: {
        label: 'Profit & Loss Statement',
        income: {
            label: 'Income',
            lineItems: {
                sales: { label: 'Gross receipt of sales' },
                returns: { label: 'Returns and allowances' },
                costOfGoods: { label: 'Cost of goods sold' },
                grossProfit: { label: 'Gross profit' },
                other: { label: 'Other income' },
                total: { label: 'Gross income' }
            }
        },
        expenses: {
            label: 'Expenses',
            lineItems: {
                advertising: { label: 'Advertising' },
                auto: { label: 'Auto' },
                contractLabor: { label: 'Contract labor' },
                depreciation: { label: 'Depreciation' },
                interest: { label: 'Interest' },
                insurance: { label: 'Insurance' },
                legal: { label: 'Legal and professional services' },
                office: { label: 'Office expenses' },
                rent: { label: 'Rent or lease' },
                repairs: { label: 'Repairs and maintenance' },
                supplies: { label: 'Supplies' },
                tax: { label: 'Taxes' },
                travel: { label: 'Travel' },
                meals: { label: 'Meals' },
                utilities: { label: 'Utilities' },
                wages: { label: 'Wages' },
                other: { label: 'Other expenses' },
                total: { label: 'Total Expenses' }
            }
        },
        netProfit: {
            label: '',
            lineItems: {
                total: { label: 'Net profit(or loss)' }
            }
        }
    },
    adminExport: {
        title: 'Prepare Export',
        description: 'Select the data sets you wish to include in the export from the list below.',
        notifications: {
            success: {
                title: 'Export complete!',
                description: 'Downloading now...'
            },
            fail: {
                title: 'Export failed!',
                description: 'There was an issue exporting the requested data!'
            }
        },
        noPermission: {
            title: 'Export feature is unavailable',
            description: 'Only CIC Super Admin users are authorized to create and download data exports.'
        }
    }
};
